/**
 * Execution Mode Hook & Context
 *
 * Allows switching between:
 * - Hot Wallet Mode: Direct execution via connected wallet (staging/testnet)
 * - Safe Mode: Generate transaction batches for multisig (production)
 *
 * Automatically detects Safe wallet connection and switches to Safe mode.
 * Uses React Context to share state across all components.
 */

import { createContext, type ReactNode, useCallback, useContext, useMemo, useState } from "react"
import type { Address } from "viem"
import { useAccount } from "wagmi"

export type ExecutionMode = "hot-wallet" | "safe"

interface ExecutionModeContextValue {
	mode: ExecutionMode
	safeAddress: Address | null
	setMode: (mode: ExecutionMode) => void
	setSafeAddress: (address: Address | null) => void
	isHotWalletMode: boolean
	isSafeMode: boolean
	/** True if connected via Safe wallet (auto-detected) */
	isConnectedViaSafe: boolean
}

const ExecutionModeContext = createContext<ExecutionModeContextValue | null>(null)

export function ExecutionModeProvider({ children }: { children: ReactNode }) {
	const [manualMode, setManualMode] = useState<ExecutionMode>("hot-wallet")
	const [safeAddress, setSafeAddressState] = useState<Address | null>(null)
	const { connector } = useAccount()

	// Auto-detect Safe wallet connection
	const isConnectedViaSafe = useMemo((): boolean => {
		if (!connector) {
			return false
		}
		return connector.id === "safe" || (connector.name?.toLowerCase().includes("safe") ?? false)
	}, [connector])

	// Effective mode: Safe if connected via Safe wallet OR manually set to Safe
	const effectiveMode = isConnectedViaSafe ? "safe" : manualMode

	const setMode = useCallback((newMode: ExecutionMode) => {
		setManualMode(newMode)
	}, [])

	const setSafeAddress = useCallback((address: Address | null) => {
		setSafeAddressState(address)
		if (address) {
			setManualMode("safe")
		}
	}, [])

	const value = useMemo<ExecutionModeContextValue>(
		() => ({
			mode: effectiveMode,
			safeAddress,
			setMode,
			setSafeAddress,
			isHotWalletMode: effectiveMode === "hot-wallet",
			isSafeMode: effectiveMode === "safe",
			isConnectedViaSafe,
		}),
		[effectiveMode, safeAddress, setMode, setSafeAddress, isConnectedViaSafe],
	)

	return <ExecutionModeContext.Provider value={value}>{children}</ExecutionModeContext.Provider>
}

export function useExecutionMode(): ExecutionModeContextValue {
	const context = useContext(ExecutionModeContext)
	if (!context) {
		throw new Error("useExecutionMode must be used within ExecutionModeProvider")
	}
	return context
}
